import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    ArrowRight,
    CheckCircle2,
    Eye,
    EyeOff,
    LockKeyhole,
    Mail,
    ShieldCheck,
    Sparkles,
    UserRound
} from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";
import toast from "react-hot-toast";
import { loginWithGoogle, register, saveSession } from "../services/authService";
import "./AuthPage.css";

function getErrorMessage(error, fallback = "Signup failed. Please try again.") {
    const apiError = error?.response?.data;

    if (typeof apiError === "string") return apiError;
    if (apiError?.message) return apiError.message;
    if (apiError?.title) return apiError.title;
    if (error?.message) return error.message;

    return fallback;
}

export default function SignupPage() {
    const location = useLocation();

    const params = new URLSearchParams(location.search);
    const returnUrl = params.get("returnUrl");
    const isPublicEntry = params.get("entry") === "public";

    const [form, setForm] = useState({
        fullName: "",
        email: "",
        password: "",
        confirmPassword: ""
    });

    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const passwordReady = form.password.length >= 6;
    const passwordsMatch = form.password && form.confirmPassword && form.password === form.confirmPassword;

    const copy = useMemo(() => {
        if (isPublicEntry) {
            return {
                eyebrow: "Client Workspace",
                title: "Create your workspace",
                subtitle: "Create your account and continue from generator to CRM without losing your preview, project, or website flow.",
                afterText: "After signup, you can manage generated websites, leads, CRM modules, and publishing."
            };
        }

        return {
            eyebrow: "Create Account",
            title: "Create your workspace",
            subtitle: "Start your Shriansh Nexus portal access with secure account ownership and role-based permissions.",
            afterText: "After signup, project access depends on assigned website ownership."
        };
    }, [isPublicEntry]);

    const updateField = (key, value) => {
        setForm((prev) => ({
            ...prev,
            [key]: value
        }));
    };

    const redirectAfterSignup = () => {
        if (returnUrl) {
            window.location.href = returnUrl;
        } else {
            window.location.href = "/projects";
        }
    };

    const validate = () => {
        if (!form.fullName.trim()) {
            toast.error("Full name is required.");
            return false;
        }

        if (!form.email.trim()) {
            toast.error("Email is required.");
            return false;
        }

        if (form.password.length < 6) {
            toast.error("Password should be at least 6 characters.");
            return false;
        }

        if (form.password !== form.confirmPassword) {
            toast.error("Passwords do not match.");
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        try {
            setLoading(true);

            const data = await register({
                name: form.fullName.trim(),
                email: form.email.trim(),
                password: form.password,
                confirmPassword: form.confirmPassword
            });

            saveSession(data);

            toast.success("Account created. Opening your workspace...");
            redirectAfterSignup();
        } catch (error) {
            toast.error(getErrorMessage(error));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        try {
            setGoogleLoading(true);

            const idToken = credentialResponse?.credential;

            if (!idToken) {
                toast.error("Google token was not received. Please try again.");
                return;
            }

            const data = await loginWithGoogle(idToken);
            saveSession(data);

            toast.success("Google account connected. Opening workspace...");
            redirectAfterSignup();
        } catch (error) {
            toast.error(getErrorMessage(error, "Google signup failed. Please try again."));
        } finally {
            setGoogleLoading(false);
        }
    };

    const handleGoogleError = () => {
        toast.error("Google signup failed. Please try again.");
    };

    return (
        <main className="auth-page">
            <div className="auth-preview-bg" aria-hidden="true">
                <div className="auth-topbar">
                    <div className="auth-mini-brand">
                        <div className="auth-mini-logo">
                            <Sparkles size={18} />
                        </div>
                        <div>
                            <strong>SHRIANSH NEXUS</strong>
                            <span>AI Growth CMS</span>
                        </div>
                    </div>

                    <div className="auth-top-actions">
                        <span>EN</span>
                        <span>Workspace</span>
                    </div>
                </div>

                <div className="auth-site-wall">
                    <div className="auth-site-card realestate">
                        <div className="site-nav">RealEstate <span>Home · Properties · Contact</span></div>
                        <h3>Discover Your Dream Property</h3>
                        <p>Premium homes, apartments, and verified property enquiries.</p>
                        <button>Search</button>
                    </div>

                    <div className="auth-site-card fitness">
                        <div className="site-nav">Fitness <span>Programs · Trainers · Pricing</span></div>
                        <h3>Transform Your Body</h3>
                        <p>Memberships, trainers, and class bookings in one website.</p>
                        <button>Join Now</button>
                    </div>

                    <div className="auth-site-card healthcare">
                        <div className="site-nav">CurePlus <span>Doctors · Services · Contact</span></div>
                        <h3>Your Health, Our Priority</h3>
                        <p>Clinic websites with doctors, services, appointments, and trust.</p>
                        <button>Book Appointment</button>
                    </div>

                    <div className="auth-site-card ecommerce">
                        <div className="site-nav">Veloura <span>Shop · Categories · Cart</span></div>
                        <h3>New Collection</h3>
                        <p>Product catalog, detail pages, and conversion focused shopping.</p>
                        <button>Shop Now</button>
                    </div>

                    <div className="auth-site-card academy">
                        <div className="site-nav">Elite Academy <span>Programs · Coaches · Admissions</span></div>
                        <h3>Build Skills. Build Champions.</h3>
                        <p>Academy websites with programs, coach profiles, and admissions.</p>
                        <button>Explore Programs</button>
                    </div>

                    <div className="auth-site-card restaurant">
                        <div className="site-nav">Harvest Table <span>Menu · Gallery · Contact</span></div>
                        <h3>Exquisite Dining Experience</h3>
                        <p>Restaurant websites with menu, gallery, and booking intent.</p>
                        <button>View Menu</button>
                    </div>

                    <div className="auth-site-card legal">
                        <div className="site-nav">LexCore <span>Practice Areas · About · Contact</span></div>
                        <h3>Your Trusted Legal Partner</h3>
                        <p>Legal websites with service authority and consultation flow.</p>
                        <button>Schedule Consultation</button>
                    </div>

                    <div className="auth-site-card beauty">
                        <div className="site-nav">Glamour Studio <span>Services · Gallery · Contact</span></div>
                        <h3>Beauty That Inspires Confidence</h3>
                        <p>Salon and spa websites with services, team, and booking CTAs.</p>
                        <button>Book Now</button>
                    </div>
                </div>

                <div className="auth-bg-vignette" />
            </div>

            <section className="auth-center-shell">
                <form className="auth-card auth-card-light auth-card-signup" onSubmit={handleSubmit}>
                    <div className="auth-card-brand">
                        <div className="auth-logo-mark">
                            <Sparkles size={22} />
                        </div>
                        <div>
                            <strong>SHRIANSH NEXUS</strong>
                            <span>{copy.eyebrow}</span>
                        </div>
                    </div>

                    <h1 className="auth-card-title">{copy.title}</h1>
                    <p className="auth-card-subtitle">{copy.subtitle}</p>

                    <div className="auth-google-wrap">
                        <GoogleLogin
                            onSuccess={handleGoogleSuccess}
                            onError={handleGoogleError}
                            text="signup_with"
                            shape="pill"
                            width="320"
                        />
                    </div>

                    {googleLoading && (
                        <div className="auth-inline-status">
                            Connecting Google account...
                        </div>
                    )}

                    <div className="auth-divider">
                        <span>or create account with email</span>
                    </div>

                    <div className="auth-field">
                        <label>Full name</label>
                        <div className="auth-input-icon-wrap">
                            <UserRound size={18} />
                            <input
                                value={form.fullName}
                                onChange={(e) => updateField("fullName", e.target.value)}
                                placeholder="Your full name"
                                type="text"
                                autoComplete="name"
                                required
                            />
                        </div>
                    </div>

                    <div className="auth-field">
                        <label>Email address</label>
                        <div className="auth-input-icon-wrap">
                            <Mail size={18} />
                            <input
                                value={form.email}
                                onChange={(e) => updateField("email", e.target.value)}
                                placeholder="you@example.com"
                                type="email"
                                autoComplete="email"
                                required
                            />
                        </div>
                    </div>

                    <div className="auth-field">
                        <label>Password</label>
                        <div className="auth-input-icon-wrap auth-password-wrap">
                            <LockKeyhole size={18} />
                            <input
                                value={form.password}
                                onChange={(e) => updateField("password", e.target.value)}
                                placeholder="Create a secure password"
                                type={showPassword ? "text" : "password"}
                                autoComplete="new-password"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword((value) => !value)}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <div className="auth-field">
                        <label>Confirm password</label>
                        <div className="auth-input-icon-wrap auth-password-wrap">
                            <LockKeyhole size={18} />
                            <input
                                value={form.confirmPassword}
                                onChange={(e) => updateField("confirmPassword", e.target.value)}
                                placeholder="Confirm your password"
                                type={showConfirmPassword ? "text" : "password"}
                                autoComplete="new-password"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword((value) => !value)}
                                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            >
                                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <div className="auth-checklist">
                        <div className={passwordReady ? "ready" : ""}>
                            <CheckCircle2 size={15} />
                            <span>At least 6 characters</span>
                        </div>
                        <div className={passwordsMatch ? "ready" : ""}>
                            <CheckCircle2 size={15} />
                            <span>Passwords match</span>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="auth-btn auth-btn-primary"
                        disabled={loading || googleLoading}
                    >
                        {loading ? "Creating account..." : "Create account"}
                        {!loading && <ArrowRight size={17} />}
                    </button>

                    <div className="auth-after-action">
                        <ShieldCheck size={17} />
                        <div>
                            <strong>After signup</strong>
                            <span>{copy.afterText}</span>
                        </div>
                    </div>

                    <div className="auth-footer-text">
                        Already have an account?{" "}
                        <Link
                            to={`/login${isPublicEntry ? "?entry=public" : ""}${returnUrl ? `${isPublicEntry ? "&" : "?"}returnUrl=${encodeURIComponent(returnUrl)}` : ""}`}
                        >
                            Login
                        </Link>
                    </div>
                </form>
            </section>
        </main>
    );
}